import { type SnapshotFrom } from 'xstate';
import { type machine } from './definition';

/**
 * Input selectors - Simple snapshot property accessors
 * These are used as inputs to memoized selectors in selectors.ts
 * Keep these simple - no computation, just property access
 */

// Context accessors
export const getContext = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context;

export const getViewType = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.viewType;

export const getChartType = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.chartType;

export const getDataset = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.dataset;

export const getContextualDataset = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.contextualDataset;

export const getLevel = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.level;

export const getCategory = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.category;

export const getFundingSource = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.fundingSource;

export const getCompareBy = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.compareBy;

export const getLevelCompare = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.levelCompare;

export const getCategoryCompare = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.categoryCompare;

export const getYearsMode = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.yearsMode;

export const getSelectedYears = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.selectedYears;

// Data accessors
export const getDatasetDataRaw = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.datasetData;

export const getMapChartDataRaw = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.mapChartData;

export const getIsMonthly = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.isMonthly;

export const getLevelOptions = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.levelOptions;

export const getCategoryOptions = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.categoryOptions;

export const getDatasetOptions = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.datasetOptions;

export const getSummary = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.summary;

export const getContextualDataRaw = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.contextualData;

export const getFundingSources = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.fundingSources;

export const getYearsOptions = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.yearsOptions;

// State matchers
export const getIsFetching = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.matches('fetchingData');

export const getIsLoading = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.hasTag('loading');

export const getStateValue = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.value;

export const getGeoData = (snapshot: SnapshotFrom<typeof machine>) =>
  snapshot.context.data.geoData;

export const getTableDimensions = (
  snapshot: SnapshotFrom<typeof machine>
): 'oneDimension' | 'twoDimensions' | 'threeDimensions' | null => {
  switch (true) {
    case snapshot.matches({ table: 'oneDimension' }):
      return 'oneDimension';
    case snapshot.matches({ table: 'twoDimensions' }):
      return 'twoDimensions';
    case snapshot.matches({ table: 'threeDimensions' }):
      return 'threeDimensions';
  }
  return null;
};
